// dependencies
import { format, isDate, parseISO } from "date-fns";
import React from "react";

import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import Avatar from "../../../../../../../../components/Avatar";
import Link from "../../../../../../../../components/Link";
import ProgressBar from "../../../../../../../../components/ProgressBar";
import Typography from "../../../../../../../../components/Text";
import TruncatedText from "../../../../../../../../components/Text/TruncatedText";

// utils
import get from "../../../../../../../../utils/GetNested";
import toEmployeeUrl from "../../../../../../../../utils/URLs/Employee";

// css
import css from "../../../Alignment.module.scss";

type GoalsLineageTableColumns = {
	id: string;
	label: string;
	width?: number;
	isSortable?: boolean;
	mobileViewHideColumn?: number;
	getter: (goal: any) => JSX.Element | string;
};

const getGoalsLineageListCols = (addGoal): Array<GoalsLineageTableColumns> => {
	return [
		{
			id: "name",
			label: "Name",
			isSortable: true,
			width: 290,
			getter: (goal: any) => {
				const { name } = goal;

				return (
					<div className={css.nameColumn} onClick={() => addGoal(goal)}>
						<TruncatedText
							maxWidth={265}
							size="paragraph-3"
							color="primary-5"
							weight="regular"
							text={name}
						/>
					</div>
				);
			},
		},
		{
			id: "public",
			label: "",
			isSortable: false,
			width: 24,
			getter: (goal: any) => {
				const transparency = get(goal, "transparency", "");

				return (
					<FontAwesomeIcon
						className={transparency === "public" ? css.isPublic : css.notPublic}
						icon={faEyeSlash}
						data-testid={faEyeSlash.iconName}
					/>
				);
			},
		},
		{
			id: "assignee.name",
			label: "Assignee",
			isSortable: true,
			width: 150,
			getter: (goal: any) => {
				const asigneeID = get(goal, ["assignee", "_id"], "");
				const asigneeName = get(goal, ["assignee", "name"], "");

				return (
					<div className={css.userBox}>
						<Avatar size={20} className={css.userAvatar} id={asigneeID} />
						<div className={css.userName}>
							<Link href={toEmployeeUrl(asigneeID)} forceHardReload={true}>
								<TruncatedText
									useTooltip="never"
									maxWidth={120}
									size="paragraph-3"
									color="primary-5"
									weight="regular"
									text={asigneeName}
								/>
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "end_date",
			label: "Due Date",
			isSortable: true,
			width: 116,
			getter: (goal: any) => {
				const dueDate = get(goal, "endDate", "");

				if (dueDate) {
					const parsedDate = parseISO(dueDate);
					return isDate(parsedDate) ? format(parsedDate, "MMM d, yyyy") : "";
				} else {
					return "";
				}
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: false,
			width: 72,
			getter: (goal: any) => {
				const statusData = get(goal, "status", "");
				const status = statusData === "done" ? "completed" : statusData;

				return status.charAt(0).toUpperCase() + status.slice(1);
			},
		},
		{
			id: "descendantsCount",
			label: "Supporting Goals",
			isSortable: false,
			width: 153,
			getter: (goal: any) => {
				const count = get(goal, "descendantsCount", "");

				return count;
			},
		},
		{
			id: "progress",
			label: "Progress",
			isSortable: true,
			width: 200,
			getter: (goal: any) => {
				const progress = get(goal, "progress", 0);
				const color = get(goal, ["outlook", "color"], null);

				return (
					<div className={css.progress}>
						<ProgressBar
							progress={progress * 100}
							width={170}
							showAmount={false}
							striped={false}
							animated={false}
							color={color}
						/>
						<Typography weight="regular" size="paragraph-3">
							{Math.floor(progress * 100)}%
						</Typography>
					</div>
				);
			},
		},
	];
};

export default getGoalsLineageListCols;
